import {AiTool} from '../tools.ts';
import type {LLMMessage, LLMRequest} from '../llm.ts';
import {MemoryCache, memoryStore} from './memory-state.ts';
import {cosineDistance, embedMemoryFields, stripHeader, updateMemory} from '../utils.ts';

const FACT_SIMILARITY_THRESHOLD = 0.62;
const DUPLICATE_THRESHOLD = 0.68;
const PROTECTED_MEMORIES = ['People/User'];
const COLLECTION_WORDS = ['project', 'projects', 'people', 'person', 'managed', 'guides', 'guide', 'research', 'class', 'classes'];

export type Memory = {
	name: string;
	description: string;
	content: string;
	embedding: number[];
	titleEmbedding?: number[];
	bodyEmbeddings?: number[][];
	links: string[];
	backlinks: string[];
}

export type MemoryRef = {
	name: string;
	description: string;
	distance?: number;
}

export type MemoryOptions = {
	memory: Memory[] | MemoryCache;
	inject?: boolean;
	tool?: boolean;
	update?: boolean;
	maxTokens?: number;
}

export type MemoryStore = Memory[] | MemoryCache;

/** Create an empty memory shell. */
function emptyNode(name: string, description = ''): Memory {
	return {name, description, content: `# ${name.split('/').pop()}\n`, embedding: [], links: [], backlinks: []};
}

function renderNode(node: Memory): string {
	return `### ${node.name}
Description: ${node.description}
Links: ${[...node.links, ...node.backlinks].join(', ') || 'none'}

\`\`\`markdown
${node.content}
\`\`\``;
}

function factSimilarity(a: Memory, b: Memory): number {
	return !a.bodyEmbeddings?.length || !b.bodyEmbeddings?.length ? 0 : Math.max(...a.bodyEmbeddings.flatMap(av => b.bodyEmbeddings!.map(bv => 1 - cosineDistance(av, bv))));
}

function words(text: string): string[] {
	return [...new Set(text.toLowerCase().replace(/[[\]()/_-]/g, ' ').replace(/[^a-z0-9\s]/g, '').split(/\s+/).filter(w => w && !COLLECTION_WORDS.includes(w)))];
}

function jaccard(a: string[], b: string[]): number {
	const bs = new Set(b), hit = a.filter(x => bs.has(x)).length, total = new Set([...a, ...b]).size;
	return total ? hit / total : 0;
}

function duplicateScore(a: Memory, b: Memory): number {
	const name = Math.max(
		jaccard(words(a.name), words(b.name)),
		jaccard(words(a.name.split('/').pop() || a.name), words(b.name.split('/').pop() || b.name)),
	);
	const desc = jaccard(words(a.description), words(b.description));
	const body = factSimilarity(a, b);
	const emb = a.embedding?.length && b.embedding?.length && a.embedding.length === b.embedding.length ? 1 - cosineDistance(a.embedding, b.embedding) : 0;
	return Math.max(body, name * 0.9 + desc * 0.06 + emb * 0.04, emb * 0.55 + name * 0.35 + desc * 0.1);
}

function homeScore(node: Memory): number {
	return (PROTECTED_MEMORIES.includes(node.name) ? 1e9 : 0)
		+ (node.name.includes('/') ? 4 : 0)
		+ (node.description && node.description !== 'Persistent memory document' ? 1 : 0)
		+ Math.min(stripHeader(node.content).length / 1000, 5);
}

function pickMerge(a: Memory, b: Memory, touched: Set<string>): [drop: Memory, home: Memory] {
	const as = homeScore(a), bs = homeScore(b);
	if(touched.has(a.name) && !touched.has(b.name)) return as > bs + 2 ? [b, a] : [a, b];
	if(touched.has(b.name) && !touched.has(a.name)) return bs > as + 2 ? [a, b] : [b, a];
	return as <= bs ? [a, b] : [b, a];
}

/** Build memory tools and memory index text. */
export function memoryTools(llm: any, memories: MemoryStore): {tools: AiTool[]; list: string} {
	const store = memoryStore(memories);
	const names = new Map<string, string>();

	for(const node of store.list)
		if(!names.has(node.name)) names.set(node.name, `${node.name} - ${node.description}`);

	for(const name of store.ghosts())
		if(!names.has(name)) names.set(name, `${name} - ghost node`);

	return {
		list: [...names.values()].join('\n'),
		tools: [
			{
				name: 'memory_search',
				description: 'Semantically search memories for most relevant',
				args: {
					query: {type: 'string', description: 'Search query', required: true},
					limit: {type: 'number', description: 'Maximum results, default 5', default: 5},
				},
				fn: async ({query, limit = 5}) => {
					if(!query?.trim()) return 'Search query is required.';
					const [chunk] = await llm.embedding(query, {maxTokens: 8000, overlapTokens: 0});
					if(!chunk?.embedding) return 'Failed to create embedding from query';
					const results = store.search(chunk.embedding, limit).map(ref => store.find(ref.name)).filter((node): node is Memory => !!node);
					return results.length ? results.map(renderNode).join('\n\n---\n\n') : 'No relevant memories found.';
				},
			},
			{
				name: 'memory_read',
				description: 'Read an entire memory document by name',
				args: {name: {type: 'string', description: 'Exact document name', required: true}},
				fn: async ({name}) => {
					const node = store.find(name);
					return node ? renderNode(node) : store.ghosts().includes(name) ? `"${name}" is a ghost node with no document of its own.` : `Not found: "${name}".`;
				},
			},
			{
				name: 'memory_delete',
				description: 'Delete a duplicate or merged memory',
				args: {name: {type: 'string', description: 'Exact document name', required: true}},
				fn: async ({name}) => {
					store.forget(name);
					return `Removed: ${name}`;
				},
			},
			{
				name: 'memory_write',
				description: 'Create or replace a memory document.',
				args: {
					name: {type: 'string', description: 'Document name following the entity naming convention.', required: true},
					description: {type: 'string', description: 'One factual sentence describing the entire document subject', required: true},
					content: {type: 'string', description: 'Complete Markdown document body, including the # title', required: true},
				},
				fn: async (args: any) => {
					const name = String(args.name || '').trim();
					if(!name) return 'A document name is required.';
					const description = String(args.description || '').trim();
					if(!description) return 'A document description is required.';
					const content = String(args.content || '').trim();
					if(!content) return 'Document content is required.';

					let node = store.find(name);
					if(!node) {
						node = emptyNode(name, description);
						if(store.cache) store.cache.add(node);
						else store.list.push(node);
					}

					node.description = name === 'People/User' ? 'All information about the current user' : description.replace(/\s+/g, ' ').trim();
					node.content = updateMemory(node, content);
					await embedMemoryFields(node, llm);
					store.cache?.commit([node]);
					return `Updated ${name}`;
				},
			},
		],
	};
}

export class MemoryManager {
	private memorized = new WeakMap<LLMMessage[], LLMMessage>();

	constructor(private llm: any) {}

	static normalize(memory?: Memory[] | MemoryCache | MemoryOptions): MemoryOptions | null {
		if(!memory) return null;
		if(Array.isArray(memory) || memory instanceof MemoryCache) return {memory, inject: true, tool: false, update: false};
		if(typeof memory === 'object' && 'memory' in memory) return {inject: true, tool: false, update: false, ...memory};
		return null;
	}

	private memorySystem(list: string): string {
		return `You maintain notes written in markdown used for memories from recent conversations using your tools.
Only preserve durable information worth remembering established by the USER.
Do not store assistant guesses, speculation, suggestions, commentary, temporary state, or details that are not worth remembering.

## Rules
- ALWAYS READ a target memory before changing it, \`memory_write\` does a full replace, it DOES NOT append!
- Memories should contain the final state, not deltas
- New conversational context is authoritative when it contracts existing information; reconcile it
- Only remove information when stale, contradicted or duplicated; always preserve existing information, formatting and keep related information together
- Only merge memories when two or more nodes are clearly about the same thing; only split a memory when it is clearly about two distinct subjects
- Use [[WikiLinks]] liberally to record aliases and relationships between entities, even ones without pages yet (ghost nodes)
- Use headings, subheadings, lists, tables and other markdown formatting to make documents clean
- Maintain a \`## Todo List\` of checkboxes AS THE FIRST SUBHEADING when an entity has tasks
	- Only create todo items for USER tasks, not AI work
	- Only store each in one place, no duplicates
	- Use  \`People/User\` for personal tasks or as a fallback

## Naming
- Every fact should be grouped with the owning entity
- Always follow the naming convention \`Collection/(Pro)Noun\`
- Facts about the user belong under People/User
- Reuse existing memories when they are clearly the same entity including aliases and ghost references.
- Only create deeper paths when there is a real parent/child entity relationship: \`School/Class/Chapter\`

Valid Examples:
- People/User
- People/John Smith
- Projects/Momentum
- Projects/Momentum/Marketing
- Research/Object Recognition
- Guides/HAM Radio SOP

## Workflow

1. Create groups of durable information and todos based on the owning entity & naming rules above
2. For each group:
  1. Read the existing memory(s)
  2. Merge the information & todos based on the rules above
  3. Write the entire patched document

Available memories:

${list || 'No memory documents exist yet.'}`;
	}

	private touchedNames(history: LLMMessage[]): string[] {
		return [...new Set(history
			.filter((h: any) => h.role === 'tool' && h.name === 'memory_write' && !h.error)
			.map((h: any) => String(h.args?.name || h.content?.match(/^Updated (.+)$/)?.[1] || '').trim())
			.filter(Boolean))];
	}

	private async backfillEmbeddings(store: ReturnType<typeof memoryStore>): Promise<void> {
		const missing = store.list.filter(m => !m.embedding?.length || !m.titleEmbedding?.length || !m.bodyEmbeddings?.length);
		await Promise.all(missing.map(m => embedMemoryFields(m, this.llm)));
		store.cache?.commit(missing);
	}

	private closestDuplicate(node: Memory, store: ReturnType<typeof memoryStore>): Memory | null {
		return store.list
			.filter(m => m.name !== node.name && !m.name.startsWith('Journal/') && !node.name.startsWith('Journal/'))
			.map(m => ({node: m, score: duplicateScore(node, m)}))
			.filter(x => x.score >= DUPLICATE_THRESHOLD || factSimilarity(node, x.node) >= FACT_SIMILARITY_THRESHOLD)
			.sort((a, b) => b.score - a.score)[0]?.node || null;
	}

	private async rehomeDeleted(drop: Memory, home: Memory, memories: MemoryStore, options: LLMRequest): Promise<void> {
		const store = memoryStore(memories);
		const backup = structuredClone(drop);
		store.forget(drop.name);

		try {
			const memory = memoryTools(this.llm, memories);
			await this.llm.ask(`A duplicate memory document was removed automatically.

Deleted document:
${renderNode(backup)}

Closest surviving home:
${renderNode(home)}

Reinsert every durable unique fact, useful relationship, alias, and user todo from the deleted document into the best remaining memory document.
Usually this should be "${home.name}", but use another existing memory if it is a better home.
Read before writing. Write full replacement documents only.
Do NOT recreate "${backup.name}" unless the deletion was wrong and it is clearly a distinct persistent entity.`, {
				model: options.memoryModel || options.model,
				temperature: 0.2,
				maxTokens: options.maxTokens,
				tools: memory.tools,
				history: [],
				system: this.memorySystem(memory.list),
			});
		} catch(err) {
			if(!store.find(backup.name)) store.cache ? store.cache.add(backup) : store.list.push(backup);
			throw err;
		} finally {
			store.cache?.commit(store.list);
		}
	}

	private async reconcileSimilar(history: LLMMessage[], memories: MemoryStore, options: LLMRequest): Promise<void> {
		const store = memoryStore(memories);
		const touched = new Set(this.touchedNames(history));
		const targets = store.list.filter(m => touched.has(m.name) || [...touched].some(t => duplicateScore(m, store.find(t) || m) >= DUPLICATE_THRESHOLD));
		const deleted = new Set<string>();
		if(!targets.length) return;
		await this.backfillEmbeddings(store);

		for(const node of targets) {
			if(!store.find(node.name) || deleted.has(node.name) || PROTECTED_MEMORIES.includes(node.name)) continue;
			const closest = this.closestDuplicate(node, store);
			if(!closest) continue;
			const [drop, home] = pickMerge(node, closest, touched);
			if(deleted.has(drop.name) || PROTECTED_MEMORIES.includes(drop.name)) continue;
			deleted.add(drop.name);
			await this.rehomeDeleted(drop, home, memories, options);
			await this.backfillEmbeddings(store);
		}
	}

	async recollect(query: string, memory: MemoryStore, limit = 15): Promise<Memory[]> {
		const store = memoryStore(memory);
		if(!store.list.length || !query?.trim()) return [];
		const [chunk] = await this.llm.embedding(query, {maxTokens: 8000, overlapTokens: 0});
		return !chunk?.embedding ? [] : store.search(chunk.embedding, limit).map(ref => store.find(ref.name)).filter((m: Memory | undefined): m is Memory => !!m);
	}

	get tools(): {read: (memory: MemoryStore) => AiTool[]} {
		return {read: (memory: MemoryStore) => memoryTools(this.llm, memory).tools};
	}

	async memorize(history: LLMMessage[], memories: Memory[] | MemoryCache, options: LLMRequest = {},): Promise<Memory[]> {
		const store = memoryStore(memories);
		const previous = this.memorized.get(history);
		let start = 0;

		if(previous) {
			const index = history.indexOf(previous);
			if(index >= 0) start = index + 1;
		}

		const turns = history.slice(start).filter((h: any) => h.role === 'user' || h.role === 'assistant');
		const conversation = turns.map((h: any) => `[${h.role}]: ${h.content}`).join('\n\n').trim();
		if(!conversation) return store.list;

		const memory = memoryTools(this.llm, memories);
		const memoryHistory: LLMMessage[] = [];

		await this.llm.ask(conversation, {
			model: options.memoryModel || options.model,
			temperature: 0.2,
			maxTokens: options.maxTokens,
			tools: memory.tools,
			history: memoryHistory,
			system: this.memorySystem(memory.list),
		});

		await this.reconcileSimilar(memoryHistory, memories, options);

		const lastTurn = turns.at(-1);
		if(lastTurn) this.memorized.set(history, lastTurn);
		return store.list;
	}
}
